import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/styles'
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Box, Grid, ButtonGroup, Button } from '@material-ui/core'
import { HeaderBlock } from '../../components/header-block/HeaderBlock'
import FileDrop from '../../components/file-drop/FileDrop'
import PageContainer from '../../components/page-container/PageContainer'
import ScrollableTabs from '../../components/tabs/Tabs';
import { Loading } from '../../components/loading/Loading';
import { PagePlaceholder } from '../../components/page-placeholder/PagePlaceholder';
import SimpleTable from '../../components/table/Table';
import { getIcon } from '../../icon/icon';
import { uploadResourceImages, getResourceImages, downloadResourceImageBlob, clearResourceImages, deleteResourceImages } from '../../state/actions/ResourceImageManagerActions';
import FileDropModal from '../../containers/file-drop-modal/FileDropModal';
import ImageGrid from '../../components/image-grid/ImageGrid';
import FileSaver from "file-saver"
import Search from '../../components/search/Search';
import { DeleteModal } from '../../containers/delete-modal/DeleteModal';
import { getResourceImageType } from '../../state/constants/ResourceImageType';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles({
    dropContainer: {
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        fontSize: "calc(10px + 2vmin)",
        padding: "16px",
    },
    downloadImage: {
        width: "20px"
    }
})

function ResourceImageManagerPage() {
    const resourceImages = useSelector(state => state.ResourceImageManagerReducer.resourceImages);
    const classes = useStyles();
    const dispatch = useDispatch();
    const [t, i18n] = useTranslation()
    const ResourceImageType = getResourceImageType()

    const [loading, setLoading] = useState(true)
    const [modalOpen, setModalOpen] = useState(false)
    const [deleteModalOpen, setDeleteModalOpen] = useState(false)
    const [currentResourceType, setCurrentResourceType] = useState(ResourceImageType.Products)

    const [searchedData, setSearchedData] = useState(Object.values(resourceImages))
    const [originalData, setOriginalData] = useState(Object.values(resourceImages))
    const [selection, setSelection] = useState([])

    const resourceTypes = Object.values(ResourceImageType).map(item => ({ key: item.value, value: item.label }))

    useEffect(() => {
        fetchData(currentResourceType)

        return () => {
            setLoading(true)
            dispatch(clearResourceImages(() => {
                setLoading(false)
            }))
        }
    }, [])

    useEffect(() => {
        setSelection([])
        setSearchedData(createData(Object.values(resourceImages)))
        setOriginalData(createData(Object.values(resourceImages)))
    }, [resourceImages])

    useEffect(() => {
        console.log(ResourceImageType)
    }, [i18n.language])

    function createData(data) {
        const newData = data.map(item => ({
            id: item.name,
            Name: item.name
        }))
        return newData
    }

    function fetchData(type) {
        setLoading(true)
        dispatch(getResourceImages(type.value, () => {
            setCurrentResourceType(type);
            setLoading(false)
        }))
    }

    function uploadImages(files, resourceType) {
        
        var resource = ResourceImageType[resourceType]

        dispatch(uploadResourceImages(files, resource.value, () => {
            fetchData(resource)
        }));
    }

    function resourceImageTypeToIndex(resourceImageType) {
        
        const resource = ResourceImageType[resourceImageType.value]

        if (resource != null) {
            return resource.key
        }

        return null
    }

    function resourceIndexToResourceImageType(index) {

        const resource = Object.values(ResourceImageType).find(item => item.key === index);
        if (resource != null) {
            return resource
        }

        return null
    }

    function onTabChange(newValue) {
        const type = resourceIndexToResourceImageType(newValue)
        fetchData(type)
    }

    function retrieveImage(imageId, callback) {
        // fetch from server
        dispatch(downloadResourceImageBlob(imageId, currentResourceType.value, (imageUrl) => {
            callback(imageId, imageUrl)
        }))
    }

    function renderData() {
        return (
            !loading ?
                <ImageGrid
                    currentSelection={selection}
                    data={searchedData}
                    spacing={5}
                    cols={5}
                    cellHeight={175}
                    emptyPlaceholder={t('resource-manager.no-image-resources')}
                    retrieveImage={(imageId, callback) => retrieveImage(imageId, callback)}
                    handleSelection={(items) => setSelection(items.map(item => ({ id: item, value: item })))}
                    selectable
                /> :
                <Loading />
        )
    }

    return (
        <Box>
            <HeaderBlock
                title={t('resource-manager.resource-image-manager')}
                right={
                    <Grid container spacing={2} alignItems="center" justify="flex-end">
                        <Grid item>
                            <Search
                                searchField={"id"}
                                returnSearchMatches={data => {
                                    setSearchedData(data)
                                }}
                                data={originalData}
                                searchTitle={t('resource-manager.search')}
                            />
                        </Grid>
                        <Grid item>
                            <ButtonGroup>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    disabled={selection.length === 0}
                                    onClick={() => {
                                        setDeleteModalOpen(true)
                                    }}
                                >
                                    {t('resource-manager.delete')}
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={() => setModalOpen(true)}
                                >
                                    {t('resource-manager.add')}
                                </Button>
                            </ButtonGroup>
                        </Grid>
                    </Grid>
                }
            />
            <PageContainer>
                <ScrollableTabs
                    handleTabChange={(newValue) => onTabChange(newValue)}
                    value={resourceImageTypeToIndex(currentResourceType)}
                    tabs={
                        [
                            {
                                key: ResourceImageType.Products.key,
                                name: ResourceImageType.Products.label,
                                content: currentResourceType.value == ResourceImageType.Products.value ? renderData() : null
                            },
                            {
                                key: ResourceImageType.Equipment.key,
                                name: ResourceImageType.Equipment.label,
                                content: currentResourceType.value == ResourceImageType.Equipment.value ? renderData() : null
                            },
                            {
                                key: ResourceImageType.Brands.key,
                                name: ResourceImageType.Brands.label,
                                content: currentResourceType.value == ResourceImageType.Brands.value ? renderData() : null
                            }
                        ]
                    }
                />
            </PageContainer>
            <FileDropModal
                title={t('resource-manager.upload')}
                open={modalOpen}
                message={t('resource-manager.add')}
                handleDone={(files, fileType) => uploadImages(files, fileType)}
                handleCancel={() => setModalOpen(false)}
                enforceTypes={resourceTypes}
                multiple
            />

            <DeleteModal
                data={selection}
                open={deleteModalOpen}
                handleCancel={() => setDeleteModalOpen(false)}
                handleDone={() => {
                    dispatch(deleteResourceImages(selection.map(item => item.id), currentResourceType.value, () => {
                        fetchData(currentResourceType)
                        setDeleteModalOpen(false)
                    }))
                }}
            />
        </Box>
    )
}


function areEqual(prevProps, nextProps) {
    const value = JSON.stringify(prevProps) === JSON.stringify(nextProps)
    return value
}

export default React.memo(ResourceImageManagerPage, areEqual)